// @flow
import * as React from 'react';
import {
  Box,
  Breadcrumbs, Button,
  Container,
  Divider, IconButton,
  Link as MuiLink,
  List,
  ListItemButton, ListItemIcon, ListItemSecondaryAction, ListItemText,
  Stack,
  Typography
} from '@mui/material';
import {
  Portal, TagsQuery,
  useDeleteTagMutation,
  useTagsQuery
} from '../../shared';
import { useState } from 'react';
import { Preloader, useConfirm } from '@coverright/features';
import { Link } from 'react-router-dom';
import { getRoute } from '../../app/router';
import { X } from '@phosphor-icons/react';
import { enqueueSnackbar } from 'notistack';
import { theme } from '../../app/theme';
import { useTagForm, withTagForm } from './tag-form';
import _ from 'lodash';

export const Tags = withTagForm(() => {
  const {data, loading} = useTagsQuery()
  const tags = data?.tags ? _.orderBy(data.tags, ['name'], ['asc']) : [];
  const {open} = useTagForm();

  return (
    <Container maxWidth={'sm'} sx={{mt: 2, pb: 10}}>
      <Breadcrumbs aria-label="breadcrumb">
        <MuiLink component={Link}
                 underline="hover" color="inherit"
                 to={`/${getRoute('profile')}`}>
          Settings
        </MuiLink>
        <Typography>Tags</Typography>
      </Breadcrumbs>

      <Divider sx={{mb: 2, mt: 1}} />

      <Stack gap={2}>
        <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant="subtitle1">Tags</Typography>

          <Button variant={'contained'} onClick={() => open()} size={'small'}>Create tag</Button>
        </Stack>

        <Portal>
          {loading && <Preloader sx={{minHeight: 300}} />}
          <List sx={{py: 0}}>
            {tags?.map((tag, i, array) => (
              <React.Fragment key={tag.id}>
                <TagItem tag={tag} onClick={() => open(tag)} />
                {i < array.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Portal>
      </Stack>
    </Container>
  );
});

const TagItem = ({tag, onClick}: {tag: TagsQuery['tags'][0], onClick: () => void}) => {
  const [disableTouchRipple, setDisableTouchRipple] = useState(false);
  const [removeTag] = useDeleteTagMutation({
    onError: () => enqueueSnackbar('You have to remove this tag from all related clients first!', {variant: "error"}),
    onCompleted: () => enqueueSnackbar('Success!', {variant: "success"}),
    refetchQueries: ['tags']
  })
  const confirm = useConfirm(theme);

  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setDisableTouchRipple(false);

    confirm({
      content: 'Are you sure you want to delete this tag?',
      okButtonTitle: 'Yes',
      cancelButtonTitle: 'No',
      title: 'Attention!',
    })
      .then(val => {
        if (val) {
          removeTag({ variables: { id: tag.id } })
        }
      })
  }

  return <ListItemButton disableTouchRipple={disableTouchRipple} onClick={onClick}>
    <ListItemIcon sx={{minWidth: 32}}>
      <Box sx={{backgroundColor: tag.color}} width={15} height={15} borderRadius={10} />
    </ListItemIcon>
    <ListItemText primary={tag.name} />
    <ListItemSecondaryAction>
      <IconButton onMouseEnter={() => setDisableTouchRipple(true)}
                  onMouseLeave={() => setDisableTouchRipple(false)}
                  onClick={handleDeleteClick}>
        <X weight={'bold'} color={'red'} size={14} />
      </IconButton>
    </ListItemSecondaryAction>
  </ListItemButton>
}
